import { FC, useEffect, useState } from 'react'
import { CircularProgress, IconButton, List, ListSubheader } from '@material-ui/core'
import { Error } from '@material-ui/icons'
import { FormattedMessage, useIntl } from 'react-intl'
import { wikiDocumentStyle } from './WikiDocument.style'
import WikiSectionComponent, { WikiSection } from './wikisection/WikiSection'
import ReactMarkdown from 'react-markdown'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'src/Store'
import { wikiActions } from 'src/actions/WikiActions'
import { appStyle } from 'src/App.style'
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos'
import { CallResult } from 'src/CustomTypes'
import { sidebarAnimationDuration } from 'src/Constants'

/**
 * Object type describing a Professor Document
 */
export interface WikiDocument {
  title: string
  logo?: string
  resume?: string
  footer?: string
  sections: WikiSection[]
  categories: string[]
}

/**
 * WikiDocumentComponentProps contains properties for the component.
 */
export interface WikiDocumentComponentProps {
  enableBackNavigation: boolean
  onBackPress: () => void
}

/**
 * WikiDocumentComponent displays information from a wiki document
 * @param props Properties for component.
 * @returns JSX component.
 */
export const WikiDocumentComponent: FC<WikiDocumentComponentProps> = (props: WikiDocumentComponentProps) => {
  const classes = wikiDocumentStyle()
  const appClasses = appStyle()
  const dispatch = useDispatch()
  const intl = useIntl()

  const openSectionIds = useSelector((state: RootState): string[] => state.wikiState.openSectionIds)
  const document = useSelector((state: RootState): WikiDocument|undefined => state.wikiState.document)
  const fetchDocumentStatus = useSelector((state: RootState): CallResult => state.wikiState.fetchDocumentStatus)

  // Used for showing spinner until the sidebare is done animating.
  const [fakeRunning, setFakeRunning] = useState<boolean>(true)

  const fetched = fetchDocumentStatus === CallResult.Succeeded
  const running = fetchDocumentStatus === CallResult.Running
  const error = fetchDocumentStatus === CallResult.Failed

  useEffect(() => {
    setTimeout(function () {
      setFakeRunning(false)
    }, sidebarAnimationDuration)
  }, [])

  return (
    <>
      <List
        component='nav'
        aria-labelledby='nested-list-subheader'
        subheader={
          <ListSubheader component='div' className={`${classes.nestedListSubheader} ${props.enableBackNavigation ? classes.backButtonPadding : ''}`} id='nested-list-subheader'>
            {props.enableBackNavigation && (
              <div className={classes.backButton}>
                <IconButton size='medium' className={classes.backButtonIcon} onClick={props.onBackPress}>
                  <ArrowBackIosIcon />
                </IconButton>
              </div>
            )}
            <div className={classes.headerTitle}>
              {fetched && document !== undefined ? document.title : ''}
            </div>
          </ListSubheader>
        }
      />
      {(running || fakeRunning) && (
        <div className={classes.progress}>
          <CircularProgress size={30} className={appClasses.progressIcon} />
          <FormattedMessage id='common.loading' />
        </div>
      )}
      {error && (
        <div className={appClasses.errorMessage}>
          <Error fontSize='large' className={appClasses.errorIcon} />
          <FormattedMessage id='common.loading.error' />
        </div>
      )}
      {fetched && document !== undefined && !fakeRunning && (
        <div className={classes.content}>
          {document.logo !== undefined && (
            <img src={document.logo} className={classes.logo} alt={intl.formatMessage({ id: 'wiki.document.logo' })} />
          )}
          <div className={classes.title}>{document.title}</div>
          {document.resume !== undefined && (
            <ReactMarkdown linkTarget='_blank' className={classes.resume}>{document.resume}</ReactMarkdown>
          )}
          {document.sections.map((section, index) => {
            return (
              <div key={index}>
                <WikiSectionComponent
                  section={section}
                  open={openSectionIds.includes(section.id)}
                  onClick={(sectionId) => {
                    if (openSectionIds.includes(sectionId)) {
                      dispatch(wikiActions.removeOpenSectionId(sectionId))
                    } else {
                      dispatch(wikiActions.addOpenSectionId(sectionId))
                    }
                  }}
                />
              </div>
            )
          })}
          {document.footer !== undefined && (
            <ReactMarkdown linkTarget='_blank' className={classes.footer}>{document.footer}</ReactMarkdown>
          )}
        </div>
      )}
    </>
  )
}

export default WikiDocumentComponent
